import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  AccessAlbumsResponse,
  ActivityActorsResponse,
  AccessCandidatesResponse,
  AccessGrantsQuery,
  AccessGrantsResponse,
  AccessRequestBody,
  ActivityResponse,
  AdminActivityQuery,
  AdminRegionsResponse,
  AdminUser,
  Album,
  AlbumDetail,
  AlbumsResponse,
  ApproveUserBody,
  CompleteUploadResponse,
  BrowseCard,
  BrowseKind,
  CreateUploadBody,
  CreateUploadResponse,
  DownloadSize,
  DownloadUrlsResponse,
  DuplicatesResponse,
  EventUsage,
  InviteView,
  InvitesResponse,
  JoinPreview,
  JoinResponse,
  MeResponse,
  NavResponse,
  Orient,
  PhotoCard,
  PhotoChangeResponse,
  PhotoDetail,
  ProfileOptionsResponse,
  ReleasePreviewResponse,
  RemoveEventResponse,
  RemovalRequest,
  RemovalRequestBody,
  RemovalRequestsResponse,
  SearchResponse,
  ShareResponse,
  SourceScope,
  StudioBadges,
  StudioCounts,
  StudioPhotoDetail,
  StudioPhotosResponse,
  StudioSort,
  Tag,
  TrashedPhotoDetail,
  TrashResponse,
  UndoResponse,
  UpdatePhotosBody,
  UpdatePhotosResponse,
  UpdateProfileBody,
  UpdateUserBody,
  Visibility,
} from '@photos/shared';
import { api, type QueryValue } from './client';

export type { BrowseKind };

export interface PhotoQuery {
  source: SourceScope;
  tags: string[];
  picksOnly: boolean;
  orient?: Orient;
}

/** Studio grid: one album (`albumId`) or every album the caller can open. */
export interface StudioQuery {
  albumId?: string;
  visibility?: Visibility;
  untagged: boolean;
  tags: string[];
  uploader?: string;
  orient?: Orient;
  sort: StudioSort;
}

export const PAGE_SIZE = 120;

export const keys = {
  me: ['me'] as const,
  tags: ['tags'] as const,
  nav: (source: SourceScope) => ['nav', source] as const,
  browse: (kind: BrowseKind, source: SourceScope) => ['browse', kind, source] as const,
  photos: (q: PhotoQuery) => ['photos', q] as const,
  photo: (id: string) => ['photo', id] as const,
  studioPhotos: (q: StudioQuery) => ['studio', 'photos', q] as const,
  studioPhoto: (id: string) => ['studio', 'photo', id] as const,
  albums: ['studio', 'albums'] as const,
  album: (id: string) => ['studio', 'album', id] as const,
  counts: ['studio', 'counts'] as const,
  badges: ['studio', 'badges'] as const,
  activity: (q: Record<string, QueryValue>) => ['studio', 'activity', q] as const,
  activityActors: ['studio', 'activity-actors'] as const,
  invites: (albumId: string) => ['studio', 'invites', albumId] as const,
  access: (q: AccessGrantsQuery) => ['studio', 'access', q] as const,
  accessAlbums: ['studio', 'access-albums'] as const,
  accessUsers: (q: string, albumId?: string) => ['studio', 'access-users', q, albumId ?? ''] as const,
  removals: ['studio', 'removal-requests'] as const,
  trash: ['studio', 'trash'] as const,
  duplicates: ['studio', 'duplicates'] as const,
  adminUsers: ['admin', 'users'] as const,
  adminRegions: ['admin', 'regions'] as const,
  adminActivity: (q: AdminActivityQuery) => ['admin', 'activity', q] as const,
  profileOptions: ['me', 'options'] as const,
  join: (token: string) => ['join', token] as const,
  share: (id: string) => ['share', id] as const,
};

/* ---------------- reads ---------------- */

export function useMe() {
  return useQuery({ queryKey: keys.me, queryFn: () => api<MeResponse>('/me'), staleTime: 60_000 });
}

export interface TagsData {
  tags: Tag[];
  aliases: Record<string, string>;
}
export function useTags(enabled = true) {
  return useQuery({
    queryKey: keys.tags,
    queryFn: () => api<TagsData>('/tags'),
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useNav(source: SourceScope, enabled = true) {
  return useQuery({
    queryKey: keys.nav(source),
    queryFn: () => api<NavResponse>('/nav', { query: { source } }),
    staleTime: 30_000,
    enabled,
  });
}

export function useBrowse(kind: BrowseKind, source: SourceScope) {
  return useQuery({
    queryKey: keys.browse(kind, source),
    queryFn: () => api<{ cards: BrowseCard[]; total: number }>(`/browse/${kind}`, { query: { source } }),
    staleTime: 30_000,
  });
}

export function photoSearchParams(q: PhotoQuery, cursor?: string) {
  return {
    source: q.source,
    tags: q.tags,
    picksOnly: q.picksOnly || undefined,
    orient: q.orient,
    cursor,
    limit: PAGE_SIZE,
  };
}

export function usePhotos(q: PhotoQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.photos(q),
    queryFn: ({ pageParam, signal }) =>
      api<SearchResponse>('/photos', { query: photoSearchParams(q, pageParam), signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.cursor ?? undefined,
    staleTime: 30_000,
    enabled,
  });
}

export function usePhoto(id: string | undefined) {
  return useQuery({
    queryKey: keys.photo(id ?? ''),
    queryFn: () => api<PhotoDetail>(`/photos/${encodeURIComponent(id!)}`),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/* ---------------- photo data (shared invalidation) ---------------- */

/** Everything derived from photo data, in Browse and in Studio. */
export function invalidatePhotoData(qc: QueryClient) {
  for (const k of ['photos', 'photo', 'nav', 'browse', 'tags', 'studio']) void qc.invalidateQueries({ queryKey: [k] });
}

export function requestDownloadUrls(ids: string[], size: DownloadSize) {
  return api<DownloadUrlsResponse>('/downloads', { method: 'POST', body: { ids, size } });
}

export function useCreateShare() {
  return useMutation({
    mutationFn: (body: { ids: string[]; expiresInDays: 7 | 30 | 90; allowDownload: 'none' | 'large' | 'original' }) =>
      api<ShareResponse>('/shares', { method: 'POST', body }),
  });
}

/** Removes the photo from every loaded result list right away (the API hides it immediately). */
export function removePhotoFromCaches(qc: QueryClient, photoId: string) {
  qc.setQueriesData<{ pages: SearchResponse[]; pageParams: unknown[] }>({ queryKey: ['photos'] }, (data) =>
    data
      ? {
          ...data,
          pages: data.pages.map((p) => {
            const items = p.items.filter((it) => it.id !== photoId);
            return items.length === p.items.length ? p : { ...p, items, total: Math.max(0, p.total - 1) };
          }),
        }
      : data,
  );
}

export function useRequestRemoval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ photoId, body }: { photoId: string; body: RemovalRequestBody }) =>
      api<RemovalRequest>(`/photos/${encodeURIComponent(photoId)}/removal-request`, { method: 'POST', body }),
    onSuccess: (_r, { photoId }) => {
      removePhotoFromCaches(qc, photoId);
      invalidatePhotoData(qc);
    },
  });
}

/* ---------------- Flock: posters manage their own posts ---------------- */

export function useFlockTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, addTags, removeTags }: { id: string; addTags: string[]; removeTags: string[] }) =>
      api<PhotoCard>(`/flock/${encodeURIComponent(id)}`, { method: 'PATCH', body: { addTags, removeTags } }),
    onSuccess: () => invalidatePhotoData(qc),
  });
}

export function useDeleteFlockPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<unknown>(`/flock/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: (_r, id) => {
      removePhotoFromCaches(qc, id);
      invalidatePhotoData(qc);
    },
  });
}

/* ---------------- uploads ---------------- */

export function createUpload(body: CreateUploadBody) {
  return api<CreateUploadResponse>('/uploads', { method: 'POST', body });
}
export function completeUploads(photoIds: string[]) {
  return api<CompleteUploadResponse>('/uploads/complete', { method: 'POST', body: { photoIds } });
}

/* ---------------- studio ---------------- */

export function useAlbums(enabled = true) {
  return useQuery({ queryKey: keys.albums, queryFn: () => api<AlbumsResponse>('/studio/albums'), staleTime: 15_000, enabled });
}

export function useAlbum(id: string) {
  return useQuery({ queryKey: keys.album(id), queryFn: () => api<AlbumDetail>(`/studio/albums/${encodeURIComponent(id)}`), staleTime: 15_000 });
}

/** Badge counts refresh every minute and when the window regains focus (and after any Studio change). */
const BADGE_REFRESH = { refetchInterval: 60_000, refetchOnWindowFocus: true } as const;

/** The Studio nav's counts (inside Studio only). */
export function useStudioCounts(enabled = true) {
  return useQuery({ queryKey: keys.counts, queryFn: () => api<StudioCounts>('/studio/counts'), enabled, staleTime: 15_000 });
}

/** The area switcher's Studio badge: a cheap call, polled. */
export function useStudioBadges(enabled = true) {
  return useQuery({ queryKey: keys.badges, queryFn: () => api<StudioBadges>('/studio/badges'), enabled, staleTime: 15_000, ...BADGE_REFRESH });
}

export function useStudioPhotos(q: StudioQuery) {
  return useInfiniteQuery({
    queryKey: keys.studioPhotos(q),
    queryFn: ({ pageParam, signal }) =>
      api<StudioPhotosResponse>('/studio/photos', {
        query: { ...q, untagged: q.untagged || undefined, cursor: pageParam, limit: PAGE_SIZE },
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.cursor ?? undefined,
    staleTime: 15_000,
  });
}

/** Possible duplicates (media): groups of likely duplicates, newest first. */
export function useDuplicates() {
  return useQuery({ queryKey: keys.duplicates, queryFn: async () => (await api<DuplicatesResponse>('/studio/duplicates')).groups, staleTime: 15_000 });
}

/** "Not duplicates": every pair in the group is remembered, so the group goes away. */
export function useDismissDuplicates() {
  return useStudioMutation((ids: string[]) => api<unknown>('/studio/duplicates/dismiss', { method: 'POST', body: { ids } }));
}

/** One photo in Trash (the read-only viewer). */
export function useTrashPhoto(id: string | undefined) {
  return useQuery({
    queryKey: [...keys.trash, id ?? ''],
    queryFn: () => api<TrashedPhotoDetail>(`/studio/trash/${encodeURIComponent(id!)}`),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useStudioPhoto(id: string | undefined) {
  return useQuery({
    queryKey: keys.studioPhoto(id ?? ''),
    queryFn: () => api<StudioPhotoDetail>(`/studio/photos/${encodeURIComponent(id!)}`),
    enabled: !!id,
    staleTime: 15_000,
  });
}

/** Any Studio change: photo data everywhere plus albums, counts and activity. */
function useStudioMutation<TVars, TRes>(fn: (v: TVars) => Promise<TRes>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidatePhotoData(qc) });
}

export function useUpdatePhotos() {
  return useStudioMutation((body: Partial<UpdatePhotosBody> & { ids: string[] }) =>
    api<UpdatePhotosResponse>('/studio/photos', { method: 'PATCH', body }),
  );
}

export function useCreateAlbum() {
  return useStudioMutation((body: { name: string; description?: string; defaultTags?: string[] }) => api<Album>('/studio/albums', { method: 'POST', body }));
}

export function useUpdateAlbum(id: string) {
  return useStudioMutation((body: { name?: string; description?: string; defaultTags?: string[] }) =>
    api<Album>(`/studio/albums/${encodeURIComponent(id)}`, { method: 'PATCH', body }),
  );
}

export function useDeleteAlbum() {
  return useStudioMutation(({ id, moveTo }: { id: string; moveTo?: string }) =>
    api<{ moved: number }>(`/studio/albums/${encodeURIComponent(id)}`, { method: 'DELETE', query: { moveTo } }),
  );
}

export function releasePreview(body: { ids: string[] } | { albumId: string }) {
  return api<ReleasePreviewResponse>('/studio/release-preview', { method: 'POST', body });
}

export function useReleaseAlbum() {
  return useStudioMutation((albumId: string) =>
    api<PhotoChangeResponse>(`/studio/albums/${encodeURIComponent(albumId)}/release`, { method: 'POST' }),
  );
}

export type AlbumAction = 'move' | 'add' | 'copy' | 'remove' | 'glean';
/** `applyTagChanges`: move/add/copy only — the album default tags follow ("Update tags" / "Also add this album's tags"). */
export function useAlbumAction() {
  return useStudioMutation(
    ({ action, albumId, ids, applyTagChanges = false }: { action: AlbumAction; albumId: string; ids: string[]; applyTagChanges?: boolean }) => {
      const album = encodeURIComponent(albumId);
      if (action === 'move')
        return api<PhotoChangeResponse>('/studio/photos/move', { method: 'POST', body: { ids, toAlbumId: albumId, applyTagChanges } });
      if (action === 'glean') return api<PhotoChangeResponse>('/studio/glean', { method: 'POST', body: { ids, albumId } });
      if (action === 'add' || action === 'copy') return api<PhotoChangeResponse>(`/studio/albums/${album}/${action}`, { method: 'POST', body: { ids, applyTagChanges } });
      return api<PhotoChangeResponse>(`/studio/albums/${album}/${action}`, { method: 'POST', body: { ids } });
    },
  );
}

export function useTrash(enabled = true) {
  return useQuery({ queryKey: keys.trash, queryFn: async () => (await api<TrashResponse>('/studio/trash')).items, enabled, staleTime: 10_000 });
}

export function useTrashAction() {
  return useStudioMutation(({ action, ids }: { action: 'restore' | 'delete'; ids: string[] }) =>
    api<PhotoChangeResponse>(`/studio/trash/${action}`, { method: 'POST', body: { ids } }),
  );
}

/** Studio activity with filters (see `activityQuery`): newest first, a page at a time. */
export function useActivity(query: Record<string, QueryValue>) {
  return useInfiniteQuery({
    queryKey: keys.activity(query),
    queryFn: ({ pageParam }) => api<ActivityResponse>('/studio/activity', { query: { ...query, cursor: pageParam } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.cursor ?? undefined,
    staleTime: 10_000,
  });
}

/** People who can appear in the log (Person filter). */
export function useActivityActors() {
  return useQuery({ queryKey: keys.activityActors, queryFn: async () => (await api<ActivityActorsResponse>('/studio/activity/actors')).items, staleTime: 60_000 });
}

export function useUndo() {
  return useStudioMutation((id: string) => api<UndoResponse>(`/studio/activity/${encodeURIComponent(id)}/undo`, { method: 'POST' }));
}

export function useInvites(albumId: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.invites(albumId),
    queryFn: async () => (await api<InvitesResponse>(`/studio/albums/${encodeURIComponent(albumId)}/invites`)).items,
    enabled,
  });
}

export function useCreateInvite(albumId: string) {
  return useStudioMutation((expiresInDays: 7 | 30 | 90) =>
    api<InviteView>(`/studio/albums/${encodeURIComponent(albumId)}/invites`, { method: 'POST', body: { expiresInDays } }),
  );
}

export function useRevokeInvite() {
  return useStudioMutation((id: string) => api<unknown>(`/studio/invites/${encodeURIComponent(id)}/revoke`, { method: 'POST' }));
}

/* ---------------- album access (media and admins) ---------------- */

export function useAccessGrants(q: AccessGrantsQuery, enabled = true) {
  return useQuery({ queryKey: keys.access(q), queryFn: () => api<AccessGrantsResponse>('/studio/access', { query: q }), enabled, staleTime: 15_000 });
}

export function useAccessAlbums(enabled = true) {
  return useQuery({ queryKey: keys.accessAlbums, queryFn: () => api<AccessAlbumsResponse>('/studio/access/albums'), enabled, staleTime: 30_000 });
}

/** Active users of type user matching `q` (name, email or church), minus those who already have `albumId`. */
export function useAccessCandidates(q: string, albumId?: string, enabled = true) {
  return useQuery({
    queryKey: keys.accessUsers(q, albumId),
    queryFn: () => api<AccessCandidatesResponse>('/studio/access/users', { query: { q, albumId } }),
    enabled,
    staleTime: 15_000,
  });
}

/** Give someone access to an album (add) or take it away (remove). */
export function useAccessChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, albumId, action }: { userId: string; albumId: string; action: 'add' | 'remove' }) =>
      action === 'add'
        ? api<AccessGrantsResponse>('/studio/access', { method: 'POST', body: { userId, albumId } })
        : api<unknown>(`/studio/access/${encodeURIComponent(albumId)}/${encodeURIComponent(userId)}`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidatePhotoData(qc);
      void qc.invalidateQueries({ queryKey: keys.adminUsers });
    },
  });
}

export function useRemovalRequests(enabled = true) {
  return useQuery({
    queryKey: keys.removals,
    queryFn: async () => (await api<RemovalRequestsResponse>('/studio/removal-requests')).items,
    enabled,
    staleTime: 15_000,
  });
}

export function useResolveRemoval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'restore' | 'remove' }) =>
      api<unknown>(`/studio/removal-requests/${encodeURIComponent(id)}`, { method: 'POST', body: { action } }),
    onSuccess: () => invalidatePhotoData(qc),
  });
}

/* ---------------- album invite links ---------------- */

export function useJoinPreview(token: string) {
  return useQuery({ queryKey: keys.join(token), queryFn: () => api<JoinPreview>(`/join/${encodeURIComponent(token)}`), retry: false });
}

export function useJoin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => api<JoinResponse>(`/join/${encodeURIComponent(token)}`, { method: 'POST', body: {} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.me });
      invalidatePhotoData(qc);
    },
  });
}

/* ---------------- access ---------------- */

/** Edit your own region, church and groups. */
export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProfileBody) => api<MeResponse>('/me/profile', { method: 'PATCH', body }),
    onSuccess: (me) => {
      qc.setQueryData(keys.me, me);
      void qc.invalidateQueries({ queryKey: keys.me });
      void qc.invalidateQueries({ queryKey: keys.adminUsers });
    },
  });
}

export function useProfileOptions() {
  return useQuery({
    queryKey: keys.profileOptions,
    queryFn: () => api<ProfileOptionsResponse>('/me/options'),
    staleTime: 5 * 60_000,
  });
}

export function useRequestAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AccessRequestBody) => api<unknown>('/me/request-access', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
}

/* ---------------- admin ---------------- */

export function useAdminUsers(enabled = true) {
  return useQuery({
    queryKey: keys.adminUsers,
    queryFn: () => api<AdminUser[]>('/admin/users'),
    enabled,
    staleTime: 15_000,
    ...BADGE_REFRESH,
  });
}

export function useAdminRegions(enabled = true) {
  return useQuery({
    queryKey: keys.adminRegions,
    queryFn: () => api<AdminRegionsResponse>('/admin/regions'),
    enabled,
    staleTime: 30_000,
  });
}

export function useAdminActivity(q: Omit<AdminActivityQuery, 'cursor'>) {
  return useInfiniteQuery({
    queryKey: keys.adminActivity(q),
    queryFn: ({ pageParam }) => api<ActivityResponse>('/admin/activity', { query: { ...q, cursor: pageParam } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.cursor ?? undefined,
    staleTime: 10_000,
  });
}

export type AdminUserAction =
  | { id: string; action: 'approve'; body: ApproveUserBody }
  | { id: string; action: 'update'; body: UpdateUserBody }
  | { id: string; action: 'deny' | 'suspend' | 'reactivate' };

export function useAdminUserAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: AdminUserAction) => {
      const path = `/admin/users/${encodeURIComponent(a.id)}`;
      if (a.action === 'update') return api<AdminUser>(path, { method: 'PATCH', body: a.body });
      return api<unknown>(`${path}/${a.action}`, { method: 'POST', body: a.action === 'approve' ? a.body : {} });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.adminUsers });
      void qc.invalidateQueries({ queryKey: keys.adminRegions });
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

/** Admin → Lists: add, rename (label only) or delete an event. */
export type EventAction = { action: 'add'; label: string } | { action: 'rename'; name: string; label: string };
export function useEventAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: EventAction) => {
      if (a.action === 'add') return api<Tag>('/admin/events', { method: 'POST', body: { label: a.label } });
      return api<Tag>(`/admin/events/${encodeURIComponent(a.name)}`, { method: 'PATCH', body: { label: a.label } });
    },
    onSuccess: () => invalidatePhotoData(qc),
  });
}

/** What uses an event (for the Remove confirmation). */
export function eventUsage(name: string) {
  return api<EventUsage>(`/admin/events/${encodeURIComponent(name)}/usage`);
}

/** Remove an event: demoted to a free tag when used, deleted when not. */
export function useRemoveEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api<RemoveEventResponse>(`/admin/events/${encodeURIComponent(name)}`, { method: 'DELETE' }),
    onSuccess: () => invalidatePhotoData(qc),
  });
}

